import { toast } from '@raystack/apsara';
import { bbox, centroid } from '@turf/turf';
import * as _ from 'lodash';
import moment from 'moment';
import { mutate } from 'swr';

import api, { createAxiosInstance } from '@src/api';
import { setblocks } from '@src/redux/_reducers/blockReducer';
import { setBounds, setCenter } from '@src/redux/_reducers/mapControl';
import {
  setWorkflowList,
  updateTaskStatus
} from '@src/redux/_reducers/workFlowReducer';
import store from '@src/redux/store';
import { CROP_PHEN_GRAPH_NAME } from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import {
  INPUT_FORMATS,
  JOB_STATUS
} from '@src/utils/constants/WORKFLOW_CONSTANTS';

const CSVToJSON = (data, delimiter = ',') => {
  const titles = data.slice(0, data.indexOf('\n')).split(delimiter);
  return data
    .slice(data.indexOf('\n') + 1)
    .split('\n')
    .map(v => {
      const values = v.split(delimiter);
      return titles.reduce(
        (obj, title, index) => ((obj[title] = values[index]), obj),
        {}
      );
    });
};

export const WorkflowListingHelper = (data, dispatch) => {
  dispatch(
    setWorkflowList(
      data.data.sort(
        (a, b) => new moment.utc(b.created_at) - new moment.utc(a.created_at)
      )
    )
  );
};

export const WorkflowListingErrorHelper = error => {
  console.error(error);
  if (error?.response?.status == 404) {
    toast.error('There are no workflows in the list', {
      description: 'There seems to be an issue with our backend services ...!'
    });
  }
};

export const BlockHelper = (data, dispatch) => {
  dispatch(setblocks(data.data));
};

export const BlockErrorHelper = error => {
  console.error(error);
  if (error?.response?.status == 404) {
    toast.error('There are no blocks present!', {
      description: 'There seems to be an issue with our backend services ...!'
    });
  }
};

export const editWorkFLowOptions = updatedWorkFlow => {
  return {
    // optimistic data displays until we populate cache
    optimisticData: workflowList => {
      // removing existing workflow
      const prevWorflows = workflowList.data.filter(workflow => {
        return workflow.id !== updatedWorkFlow.id;
      });
      store.dispatch(
        setWorkflowList(
          [...prevWorflows, updatedWorkFlow].sort(
            (a, b) =>
              new moment.utc(b.created_at) - new moment.utc(a.created_at)
          )
        )
      );
      return {
        data: [...prevWorflows, updatedWorkFlow].sort(
          (a, b) => new moment.utc(b.created_at) - new moment.utc(a.created_at)
        ),
        err: ''
      };
    },
    rollbackOnError: true,
    populateCache: (updatedWorkFlow, workflowList) => {
      const prevWorflows = workflowList.data.filter(workflow => {
        return workflow.id !== updatedWorkFlow?.data.id;
      });
      store.dispatch(
        setWorkflowList(
          [...prevWorflows, updatedWorkFlow.data].sort(
            (a, b) =>
              new moment.utc(b.created_at) - new moment.utc(a.created_at)
          )
        )
      );
      return {
        data: [...prevWorflows, updatedWorkFlow.data].sort(
          (a, b) => new moment.utc(b.created_at) - new moment.utc(a.created_at)
        ),
        err: ''
      };
    },
    revalidate: false
  };
};

export const deleteWorkflowOptions = workflowIdToDelete => {
  return {
    optimisticData: workflowList => {
      const updatedWorkflowList = workflowList.data.filter(
        workflow => workflow.id !== workflowIdToDelete
      );
      store.dispatch(setWorkflowList(updatedWorkflowList));
      return {
        data: [...updatedWorkflowList].sort(
          (a, b) => new moment.utc(b.created_at) - new moment.utc(a.created_at)
        ),
        err: ''
      };
    },
    rollbackOnError: true,
    populateCache: (emptyObj, workflowList) => {
      const updatedWorkflowList = workflowList.data.filter(
        workflow => workflow.id !== workflowIdToDelete
      );
      store.dispatch(setWorkflowList(updatedWorkflowList));
      return {
        data: [...updatedWorkflowList].sort(
          (a, b) => new moment.utc(b.created_at) - new moment.utc(a.created_at)
        ),
        err: ''
      };
    },
    revalidate: false
  };
};

export function generateUniqueColors(n) {
  let colors = new Set();
  while (colors.size < n) {
    let color = '#' + Math.floor(Math.random() * 16777215).toString(16);
    let Reg_Exp = /^#[0-9A-F]{6}$/i;
    if (Reg_Exp.test(color)) {
      colors.add(color);
    }
  }
  return Array.from(colors);
}

export const GenerateClusterColorMap = n => {
  let colors = generateUniqueColors(n);
  let colormap = {};
  colors.map((hex, i) => {
    colormap[`${i + 1}`] = hex;
  });
  return encodeURIComponent(JSON.stringify(colormap));
};

// fetch tabular data content from dexter
export async function fetchTabData(
  outputs,
  setData,
  setLoader,
  activeAoi = null
) {
  setLoader(true);
  const activeWorkflow = store.getState().workflow.activeWorkflow;
  let project_id =
    activeAoi === null ? activeWorkflow.project_id : activeAoi.project_id;
  const isArray = typeof outputs == Array;
  let tabularData = isArray
    ? outputs.filter(
        output => output.format === INPUT_FORMATS.FormatTypeTabular
      )
    : Object.keys(outputs)
        .map(output => outputs[output])
        .filter(output => output.format === INPUT_FORMATS.FormatTypeTabular);

  let CSVData = {};
  try {
    const results = await Promise.all(
      tabularData.map(async item => {
        const response = await api.dexter.post(
          `/v1alpha1/asset/?project_id=${project_id}`,
          {
            url: item.value
          }
        );
        return response.data;
      })
    );

    tabularData.map((tab, i) => {
      CSVData[tab.name] = CSVToJSON(results[i]);
    });

    setData(CSVData);
  } catch (error) {
    console.error(error);
    toast.error('Unable to fetch job data!', {
      description:
        'There seems to be an issue fetching job result, please try again!'
    });
  } finally {
    setLoader(false);
  }
}
export async function fetchJsonDataCD(
  outputs,
  setData,
  setLoader,
  activeAoi = null
) {
  setLoader(true);
  const activeWorkflow = store.getState().workflow.activeWorkflow;

  // fetch AOI ID from workflow source block
  let project_id =
    activeAoi === null ? activeWorkflow.project_id : activeAoi.project_id;

  let tabularData = Object.keys(outputs)
    .map(output => outputs[output])
    .filter(output =>
      ['raster_stats', 'path3_diff', 'categorical_stats'].includes(output.name)
    );

  let GraphData = {};

  try {
    const results = await Promise.all(
      tabularData.map(async item => {
        const response = await api.dexter.post(
          `/v1alpha1/asset/?project_id=${project_id}`,
          {
            url: item.value
          }
        );
        return response.data;
      })
    );

    tabularData.map((tab, i) => {
      if (typeof results[i] === 'object') GraphData[tab.name] = results[i];
      else {
        let data = CSVToJSON(results[i]);
        // parse the json to generate the data format required by CDCommon component
        GraphData[tab.name] = data.reduce((acc, item) => {
          Object.keys(item).forEach(key => {
            acc[key] = [...(acc?.[key] || []), item[key]];
            acc[key] = acc[key].filter(
              item => !isNaN(item) || item !== undefined
            );
          });
          return acc;
        }, {});
      }
    });
    setData(GraphData);
  } catch (error) {
    console.error(error);
    toast.error('Unable to fetch job data!', {
      description:
        'There seems to be an issue fetching job result, please try again!'
    });
  } finally {
    setLoader(false);
  }
}
export async function fetchJsonData(
  outputs,
  setData,
  setLoader,
  activeAoi = null
) {
  setLoader(true);
  const activeWorkflow = store.getState().workflow.activeWorkflow;
  let project_id =
    activeAoi === null ? activeWorkflow.project_id : activeAoi.project_id;

  let tabularData = Object.keys(outputs)
    .map(output => outputs[output])
    .filter(output => output.name === CROP_PHEN_GRAPH_NAME);

  let GraphData = {};

  try {
    const results = await Promise.all(
      tabularData.map(async item => {
        const response = await api.dexter.post(
          `/v1alpha1/asset/?project_id=${project_id}`,
          {
            url: item.value
          }
        );

        return response.data;
      })
    );

    tabularData.map((tab, i) => {
      GraphData[tab.name] = results[i];
    });
    setData(GraphData);
  } catch (error) {
    console.error(error);
    toast.error('Unable to fetch job data!', {
      description:
        'There seems to be an issue fetching job result, please try again!'
    });
  } finally {
    setLoader(false);
  }
}

// Workflow output handler

export async function fetchVectorData(
  output,
  setVectorData,
  setLoader,
  isInsight = null,
  activeAoi
) {
  const activeWorkflow = store.getState().workflow.activeWorkflow;
  const project_id =
    isInsight === null ? activeWorkflow.project_id : activeAoi.project_id;

  setLoader(true);
  try {
    const outputVector = await api.dexter.post(
      `/v1alpha1/asset/?project_id=${project_id}`,
      {
        url: output.value
      }
    );
    setVectorData(outputVector.data);
  } catch (error) {
    console.error(error);
    toast.error('Unable to fetch job data!', {
      description:
        'There seems to be an issue fetching job result, please try again!'
    });
  } finally {
    setLoader(false);
  }
}

export async function SetOutputHandler(setLoader, setJobBounds, setOutputs) {
  const activeJob = store.getState().workflow.activeJob;
  const activeWorkflow = store.getState().workflow.activeWorkflow;

  // fetch AOI ID from workflow source block
  let aoi_ids = [];
  activeJob &&
    activeJob.spec.components.map(block => {
      if (block.name.startsWith('aoi') || block.name.startsWith('AOI')) {
        let aoi_id = block.inputs.filter(i => i.name === 'aoi_id')[0].value;
        aoi_ids.push(aoi_id);
      }
    });

  setLoader(true);
  try {
    const res = await api.dataStore.get(
      `/${activeWorkflow?.project_id}/aoi/${aoi_ids[0]}`
    );

    // adjust map viewport parameters
    let staCenter = centroid(res.data.geometry).geometry.coordinates;
    let stacBounds = bbox(res.data.geometry);

    setJobBounds(res.data.geometry);

    // setoutputs array
    let results = {};
    activeJob &&
      activeJob?.tasks.map(task => {
        if (!task.name.startsWith('aoi')) {
          let block_id = activeJob.spec.components.filter(
            i => i.name === task.name
          )[0].block_id;

          let inputs = task.inputs;
          task.result.map(output => {
            results[`${output.value}`] = {
              ...output,
              block_id: block_id,
              inputs: inputs
            };
          });
        }
      });

    setOutputs(results);

    const timer = setTimeout(() => {
      store.dispatch(setCenter([staCenter[1], staCenter[0]]));
      store.dispatch(
        setBounds([
          [stacBounds[0], stacBounds[1]],
          [stacBounds[2], stacBounds[3]]
        ])
      );
    }, 80);

    return () => clearTimeout(timer);
  } catch (error) {
    console.error(error);
  } finally {
    setLoader(false);
  }
}

const ViewPortHandler = aoi => {
  const staCenter = centroid(aoi).geometry.coordinates;
  const stacBounds = bbox(aoi);
  const timer = setTimeout(() => {
    store.dispatch(setCenter([staCenter[1], staCenter[0]]));
    store.dispatch(
      setBounds([
        [stacBounds[0], stacBounds[1]],
        [stacBounds[2], stacBounds[3]]
      ])
    );
  }, 80);
  return () => clearTimeout(timer);
};

async function GetVectorViewport(
  selectedComponent,
  activeTask,
  setJobBounds,
  inputVector,
  inputs,
  setOutputs,
  setLoader
) {
  setLoader(true);

  const activeWorkflow = store.getState().workflow.activeWorkflow;
  try {
    const outputVector = await api.dexter.post(
      `/v1alpha1/asset/?project_id=${activeWorkflow.project_id}`,
      {
        url: inputVector.value
      }
    );

    setJobBounds(outputVector.data);

    // setoutputs array
    let results = {};
    if (!_.isEmpty(selectedComponent)) {
      activeTask.result.map(output => {
        results[`${output.value}`] = {
          ...output,
          block_id: selectedComponent.block_id,
          inputs: inputs
        };
      });
      setOutputs(results);
    } else {
      setOutputs({});
      throw new Error('comp-error');
    }

    ViewPortHandler(outputVector.data);
  } catch (error) {
    console.error(error);
  } finally {
    setLoader(false);
  }
}

async function GetRasterViewport(
  selectedComponent,
  activeTask,
  setJobBounds,
  inputRaster,
  inputs,
  setOutputs,
  setLoader
) {
  setLoader(true);
  try {
    // call titiler/cog/info to fetch GEOJSON bounds on input raster
    // on processing block -> faster response as TiTiiler caches the
    // subsequent requests to the same file
    let res;
    if (inputRaster?.stac_url?.length > 0) {
      const TiTilerStac = createAxiosInstance(import.meta.env.VITE_STAC_TILER);
      res = await TiTilerStac.get(
        `/stac/info.geojson?url=${inputRaster.stac_url}`
      );
    } else {
      const TiTilerCog = createAxiosInstance(import.meta.env.VITE_COG_TILER);
      res = await TiTilerCog.get(`/cog/info.geojson?url=${inputRaster.value}`);
    }

    setJobBounds(res.data.geometry);

    // setoutputs array
    let results = {};
    if (!_.isEmpty(selectedComponent)) {
      activeTask.result.map(output => {
        results[`${output.value}`] = {
          ...output,
          block_id: selectedComponent.block_id,
          inputs: inputs
        };
      });
      setOutputs(results);
    } else {
      setOutputs({});
      throw new Error('comp-error');
    }
    ViewPortHandler(res.data.geometry);
  } catch (error) {
    console.error(error);
  } finally {
    setLoader(false);
  }
}
export async function SetOutputHandlerV2(
  setLoader,
  setJobBounds,
  setOutputs,
  activeTask
) {
  const activeJob = store.getState().workflow.activeJob;
  const inputs = activeTask.inputs;
  // fetch raster input from block, no fallback required as if raster is
  // not found the it will be undefined
  const inputRaster = inputs.find(i => i.format === INPUT_FORMATS.FormatRaster);

  const inputVector = inputs.find(i => i.format === INPUT_FORMATS.FormatVector);

  const selectedComponent =
    activeJob && !_.isEmpty(activeJob)
      ? activeJob.spec.components.find(i => i.name === activeTask.name)
      : {};

  // undefined check to force code block if selectedCOmponent has input
  // of raster
  if (inputRaster) {
    GetRasterViewport(
      selectedComponent,
      activeTask,
      setJobBounds,
      inputRaster,
      inputs,
      setOutputs,
      setLoader
    );
    return;
  }

  if (inputVector) {
    // if input is only vector
    GetVectorViewport(
      selectedComponent,
      activeTask,
      setJobBounds,
      inputVector,
      inputs,
      setOutputs,
      setLoader
    );
    return;
  }
}

export async function JobPollHelper(data) {
  const state = store.getState();
  const activeWorkflow = state.workflow.activeWorkflow;
  const activeJob = state.workflow.activeJob;

  // udpate task status of activeJob
  if (JOB_STATUS.inProgress.includes(data.status)) {
    if (activeJob.id === data.id) {
      // update all task statuses
      store.dispatch(updateTaskStatus(data.tasks));
      return data;
    }
  }

  // condition for succes and failure
  if (
    JOB_STATUS.completed.includes(data.status) ||
    JOB_STATUS.failed.includes(data.status)
  ) {
    // udpate job status's in list
    mutate(`/v1alpha1/workflows/${activeWorkflow.id}/jobs`);
    // get latest state of active Job if polling job is an active job
    if (activeJob.id === data.id) {
      let selectedJob = data;
      let jobBTN = document.getElementById(`job-${selectedJob?.id}`);
      jobBTN.click();
    }
    return data;
  }
}
